import React from 'react';
import Icon from '../../../components/AppIcon';

/**
 * 📊 STATS BAR - Quick Metrics at a Glance
 * 
 * Mobile: 2 columns, 4 cards stacked
 * Tablet/Desktop: 4 columns, all visible
 * 
 * Shows:
 * - Credit Hours (💰 PAY)
 * - Block Hours (⏱️ FLIGHT TIME)
 * - Duty Hours (📋 WORK TIME)
 * - Estimated Pay (💵 $$$)
 * 
 * Visual: Gradient backgrounds, icons, comparison to target
 */

const StatsBar = ({ stats, pay, hourlyRate, isLoadingRate }) => {
  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatHours = (hours) => {
    if (!hours) return '0h';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  // Use real hourly rate from Supabase or fallback to $250
  const effectiveRate = hourlyRate || 250.00;
  const usingPlaceholder = !hourlyRate;
  
  // Calculate estimated pay with real rate
  const creditHours = stats?.credit_hours || 0;
  const estimatedPay = creditHours * effectiveRate;

  const stats_data = [
    {
      label: 'Credit Hours',
      value: stats?.credit_hours || 0,
      formatted: formatHours(stats?.credit_hours || 0),
      icon: 'DollarSign',
      gradient: 'from-primary to-accent-cyan',
      textColor: 'text-primary',
      bgColor: 'bg-primary/20',
      subtitle: 'For pay calc',
      target: 75, // Typical line value
      targetLabel: 'ALV'
    },
    {
      label: 'Block Hours',
      value: stats?.block_hours || 0,
      formatted: formatHours(stats?.block_hours || 0),
      icon: 'Clock',
      gradient: 'from-accent-cyan to-accent-purple',
      textColor: 'text-accent-cyan',
      bgColor: 'bg-accent-cyan/20',
      subtitle: 'Actual flight',
      target: null
    },
    {
      label: 'Duty Hours',
      value: stats?.duty_hours || 0,
      formatted: formatHours(stats?.duty_hours || 0),
      icon: 'Briefcase',
      gradient: 'from-warning to-error',
      textColor: 'text-warning',
      bgColor: 'bg-warning/20',
      subtitle: 'Report to release',
      target: null
    },
    {
      label: 'Est. Pay',
      value: estimatedPay,
      formatted: isLoadingRate ? 'Loading...' : formatCurrency(estimatedPay),
      icon: 'Banknote',
      gradient: 'from-success to-warning',
      textColor: 'text-success',
      bgColor: 'bg-success/20',
      subtitle: isLoadingRate 
        ? 'Fetching rate...' 
        : `$${effectiveRate.toFixed(2)}/hr${usingPlaceholder ? ' (placeholder)' : ''}`,
      target: null
    }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
      {stats_data.map((stat, idx) => (
        <div
          key={idx}
          className="card p-4 relative overflow-hidden group hover:shadow-lg transition-all duration-300"
        >
          {/* Gradient Background (subtle) */}
          <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-5 group-hover:opacity-10 transition-opacity`} />
          
          {/* Content */}
          <div className="relative">
            {/* Icon + Label */}
            <div className="flex items-center justify-between mb-2">
              <div className={`w-8 h-8 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                <Icon name={stat.icon} className={`w-4 h-4 ${stat.textColor}`} />
              </div>
              
              {/* Target indicator (if applicable) */}
              {stat.target && (
                <div className="text-xs text-muted-foreground">
                  {stat.targetLabel}: {stat.target}h
                </div>
              )}
            </div>

            {/* Value */}
            <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">
              {stat.formatted}
            </div>

            {/* Label + Subtitle */}
            <div>
              <div className="text-sm font-medium text-foreground">
                {stat.label}
              </div>
              <div className="text-xs text-muted-foreground">
                {stat.subtitle}
              </div>
            </div>

            {/* Progress bar (for credit hours vs target) */}
            {stat.target && stat.value > 0 && (
              <div className="mt-3 pt-2 border-t border-border">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Progress</span>
                  <span>{Math.round((stat.value / stat.target) * 100)}%</span>
                </div>
                <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${stat.gradient} transition-all duration-500`}
                    style={{ width: `${Math.min((stat.value / stat.target) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default StatsBar;
